import React, { useState, useMemo, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import SEO from "../components/SEO";
import Reveal from "../components/Reveal";
import Breadcrumbs from "../components/Breadcrumbs";
import { useI18n, STR } from "../i18n";
import { CATEGORIES, PRODUCTS, IMAGES } from "../mock";
import { CheckCircle2, ArrowRight, MessageCircle, FileDown } from "lucide-react";
import { COMPANY } from "../mock";

export default function Catalog() {
  const { t } = useI18n();
  const [active, setActive] = useState("all");
  const loc = useLocation();

  useEffect(() => {
    if (loc.hash) {
      const id = loc.hash.replace("#", "");
      if (CATEGORIES.some(c => c.id === id)) {
        setActive(id);
        setTimeout(() => document.getElementById("cat-list")?.scrollIntoView({ behavior: "smooth" }), 100);
      }
    }
  }, [loc.hash]);

  const filtered = useMemo(() => active === "all" ? PRODUCTS : PRODUCTS.filter(p => p.cat === active), [active]);

  return (
    <>
      <SEO title={{ es: "Catálogo", en: "Catalog" }} path="/catalogo" description={{ es: "Catálogo: equipos de bombeo, válvulas, cabezal de pozo, transformadores, estructuras de transmisión y equipos de protección personal.", en: "Catalog: pumping equipment, valves, wellhead, transformers, transmission structures and personal protective equipment." }} />
      <section className="hero phero">
        <div className="bg" style={{ backgroundImage: `url(${IMAGES.pumps1})` }} />
        <div className="shade" />
        <div className="container-x inner">
          <Breadcrumbs items={[{ label: { es: "Inicio", en: "Home" }, href: "/" }, { label: { es: "Catálogo", en: "Catalog" } }]} />
          <span className="eyebrow">{t({ es: "Catálogo", en: "Catalog" })}</span>
          <h1>{t({ es: "Equipos y suministros por categoría", en: "Equipment & supplies by category" })}</h1>
          <div style={{ marginTop: 22 }}>
            <a href="/catalogo-grupo-inversiones-altamira.pdf" target="_blank" rel="noopener noreferrer" className="btn btn-gold" data-testid="download-catalog-button">
              <FileDown size={16} /> {t({ es: "Ver catálogo PDF", en: "View PDF catalog" })}
            </a>
          </div>
        </div>
      </section>

      {/* Category cards */}
      <section className="section">
        <div className="container-x">
          <div className="grid-2">
            {CATEGORIES.map((c, i) => (
              <Reveal key={c.id} delay={i * 60}>
                <div className="card" id={c.id}>
                  <div className="img" style={{ backgroundImage: `url(${c.image})`, aspectRatio: "16/8" }} />
                  <div className="body">
                    {c.secondary && <span className="chip" style={{ fontSize: 11, fontWeight: 700, background: "#f1f5f9", color: "#475569", marginBottom: 8, display: "inline-block" }}>{t({ es: "Bajo pedido", en: "On request" })}</span>}
                    <h3>{t(c.title)}</h3>
                    <p>{t(c.short)}</p>
                    <ul style={{ listStyle: "none", padding: 0, margin: "12px 0" }}>
                      {t(c.features).map((f, k) => <li key={k} style={{ display: "flex", alignItems: "center", gap: 8, padding: "4px 0", fontSize: 14 }}><CheckCircle2 size={16} style={{ color: "var(--gold)" }} />{f}</li>)}
                    </ul>
                    <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                      <button onClick={() => { setActive(c.id); document.getElementById("cat-list").scrollIntoView({ behavior: "smooth" }); }} className="btn btn-navy" style={{ fontSize: 13, padding: "10px 16px" }}>{t(STR.cta.viewMore)} <ArrowRight size={14} /></button>
                      <a href={`https://wa.me/${COMPANY.phones[0].wa}?text=${encodeURIComponent(t({ es: `Hola, me interesa: ${t(c.title)}`, en: `Hi, I'm interested in: ${t(c.title)}` }))}`} target="_blank" rel="noopener noreferrer" className="btn btn-wa" style={{ fontSize: 13, padding: "10px 16px" }}><MessageCircle size={14} /> {t(STR.cta.quote)}</a>
                    </div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>

          {/* Product list */}
          <div id="cat-list" style={{ marginTop: 80 }}>
            <Reveal className="sh"><span className="eyebrow">{t({ es: "Productos destacados", en: "Featured products" })}</span><h2>{t({ es: "Modelos y referencias", en: "Models & references" })}</h2></Reveal>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 10, justifyContent: "center", marginBottom: 30 }}>
              <button onClick={() => setActive("all")} className={`btn ${active === "all" ? "btn-navy" : "btn-outline"}`} style={{ fontSize: 13, padding: "8px 14px" }}>{t({ es: "Todos", en: "All" })}</button>
              {CATEGORIES.map(c => (
                <button key={c.id} onClick={() => setActive(c.id)} className={`btn ${active === c.id ? "btn-navy" : "btn-outline"}`} style={{ fontSize: 13, padding: "8px 14px" }}>{t(c.title)}</button>
              ))}
            </div>
            <div className="grid-3">
              {filtered.map((p, i) => (
                <Reveal key={p.id} delay={i * 60}>
                  <div className="card">
                    <Link to={`/catalogo/${p.slug}`} data-testid="product-card-link" style={{ display: "block" }}>
                      <div className="img" style={{ backgroundImage: `url(${p.image})` }} />
                    </Link>
                    <div className="body">
                      <Link to={`/catalogo/${p.slug}`} style={{ color: "inherit" }}><h3 style={{ fontSize: 20 }}>{t(p.title)}</h3></Link>
                      <p style={{ marginBottom: 8 }}>{t(p.spec)}</p>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 12, gap: 8, flexWrap: "wrap" }}>
                        <Link to={`/catalogo/${p.slug}`} style={{ color: "var(--navy)", fontWeight: 700, fontSize: 13, textTransform: "uppercase", letterSpacing: "0.05em" }}>{t({ es: "Ver ficha", en: "View details" })} <ArrowRight size={13} style={{ display: "inline", verticalAlign: "-2px" }} /></Link>
                        <Link to={`/contacto?producto=${encodeURIComponent(t(p.title))}&categoria=${encodeURIComponent(t(CATEGORIES.find(c => c.id === p.cat).title))}`} className="btn btn-navy" style={{ fontSize: 12, padding: "8px 12px" }}>{t(STR.cta.quote)}</Link>
                      </div>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
